// FILE: src/Components/Copilot/CopilotHistoryDrawer.js
import React, { useMemo, useState } from "react";
import { Drawer, Empty, Input, Modal, Spin } from "antd";

const getSessionId = (session) => {
  return session?._id || session?.id || session?.sessionId || null;
};

const getSessionDate = (session) => {
  return session.lastMessageAt || session.updatedAt || session.createdAt || null;
};

const getGroupLabel = (dateValue) => {
  if (!dateValue) return "Older";

  const date = new Date(dateValue);
  const now = new Date();
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((nowStart - dateStart) / (24 * 60 * 60 * 1000));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";

  if (diffDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: "long" });
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const groupedItems = (items) => {
  return items.reduce((acc, item) => {
    const group = getGroupLabel(getSessionDate(item));

    if (!acc[group]) {
      acc[group] = [];
    }

    acc[group].push(item);

    return acc;
  }, {});
};

const ActionMenu = ({ session, onRename, onDelete }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        className="flex h-7 w-7 items-center justify-center rounded-full text-white/35 transition hover:bg-white/[0.08] hover:text-white"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <i className="fa-solid fa-ellipsis text-xs"></i>
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-50 w-40 rounded-xl bg-[#1b1d21] p-1.5 shadow-2xl ring-1 ring-white/[0.08]">
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-white/80 transition hover:bg-white/[0.07] hover:text-white"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onRename?.(session);
            }}
          >
            <i className="fa-solid fa-pencil w-4 text-white/70"></i>
            Rename
          </button>

          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-red-200 transition hover:bg-red-500/[0.12]"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onDelete?.(session);
            }}
          >
            <i className="fa-regular fa-trash-can w-4 text-red-200/80"></i>
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const HistoryList = ({
  items,
  loading,
  selectedSessionId,
  onSelectSession,
  onRenameSession,
  onDeleteSession,
}) => {
  const grouped = useMemo(() => groupedItems(items), [items]);

  if (loading) {
    return (
      <div className="flex h-56 items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="flex h-56 items-center justify-center">
        <Empty description={<span className="text-white/45">No chats found.</span>} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([groupName, groupItems]) => (
        <div key={groupName}>
          <h3 className="mb-2 px-1 text-sm font-black text-white/38">
            {groupName}
          </h3>

          <div className="space-y-1">
            {groupItems.map((item) => {
              const sessionId = getSessionId(item);
              const isActive =
                String(selectedSessionId || "") === String(sessionId || "");

              return (
                <div
                  key={sessionId}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectSession?.(item)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectSession?.(item);
                    }
                  }}
                  className={`group flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left transition ${
                    isActive
                      ? "bg-white/[0.085]"
                      : "hover:bg-white/[0.045]"
                  }`}
                >
                  <div className="min-w-0">
                    <p className="mb-0 truncate text-sm font-bold leading-snug text-white">
                      {item.title || "New Copilot chat"}
                    </p>
                    <p className="mb-0 mt-0.5 text-[11px] font-semibold text-white/38">
                      {item.messageCount || 0} message
                      {Number(item.messageCount || 0) === 1 ? "" : "s"}
                    </p>
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-1 opacity-70 transition group-hover:opacity-100">
                    

                    <ActionMenu
                      session={item}
                      onRename={onRenameSession}
                      onDelete={onDeleteSession}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function CopilotHistoryDrawer({
  open,
  onClose,
  sessions = [],
  loading = false,
  selectedSessionId = null,
  onSelectSession,
  onNewChat,
  onRefresh,
  onRenameSession,
  onDeleteSession,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renamingSession, setRenamingSession] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);

  const filteredChats = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return sessions;
    }

    return sessions.filter((item) => {
      return String(item.title || "").toLowerCase().includes(query);
    });
  }, [sessions, searchTerm]);

  const openRenameModal = (session) => {
    setRenamingSession(session);
    setRenameValue(session?.title || "New Copilot chat");
    setRenameModalOpen(true);
  };

  const closeRenameModal = () => {
    if (renameSaving) return;

    setRenameModalOpen(false);
    setRenamingSession(null);
    setRenameValue("");
  };

  const submitRename = async () => {
    const sessionId = getSessionId(renamingSession);
    const cleanTitle = renameValue.trim();

    if (!sessionId || !cleanTitle) return;

    setRenameSaving(true);

    try {
      await onRenameSession?.(renamingSession, cleanTitle);
      closeRenameModal();
    } finally {
      setRenameSaving(false);
    }
  };

  const confirmDeleteSession = (session) => {
    Modal.confirm({
      title: "Delete this Copilot chat?",
      content: "This removes the chat from your history. This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        await onDeleteSession?.(session);
      },
    });
  };

  return (
    <>
      <style>
        {`
          .copilot-history-drawer .ant-drawer-content {
            background: #121418 !important;
            color: #ffffff !important;
            border-left: 1px solid rgba(255,255,255,0.055) !important;
          }

          .copilot-history-drawer .ant-drawer-body {
            padding: 0 !important;
            overflow: hidden !important;
          }

          .copilot-history-drawer .ant-drawer-content-wrapper {
            max-width: 100vw !important;
            box-shadow: -18px 0 60px rgba(0,0,0,0.38) !important;
          }

          .copilot-history-scroll {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          .copilot-history-scroll::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }

          .copilot-history-search .ant-input {
            background: transparent !important;
            color: #ffffff !important;
            font-size: 14px !important;
            font-weight: 600 !important;
          }

          .copilot-history-search .ant-input::placeholder {
            color: rgba(255,255,255,0.28) !important;
          }

          .copilot-history-drawer .ant-empty-description {
            color: rgba(255,255,255,0.42) !important;
          }

          .copilot-rename-modal .ant-modal-content {
            border: 1px solid rgba(255,255,255,0.07) !important;
            border-radius: 14px !important;
            background: #181a1f !important;
            color: #ffffff !important;
            box-shadow: 0 24px 70px rgba(0,0,0,0.52) !important;
          }

          .copilot-rename-modal .ant-modal-header {
            background: #181a1f !important;
          }

          .copilot-rename-modal .ant-modal-title {
            color: #ffffff !important;
          }

          .copilot-rename-modal .ant-input {
            border-color: rgba(255,255,255,0.1) !important;
            border-radius: 9px !important;
            background: rgba(255,255,255,0.055) !important;
            color: #ffffff !important;
            font-weight: 600 !important;
          }

          .copilot-rename-modal .ant-input:hover,
          .copilot-rename-modal .ant-input:focus {
            border-color: rgba(255,255,255,0.18) !important;
            box-shadow: none !important;
          }
        `}
      </style>

      <Drawer
        open={open}
        onClose={onClose}
        placement="right"
        width={380}
        closeIcon={null}
        className="copilot-history-drawer"
        destroyOnClose={false}
      >
        <div className="flex h-full min-h-0 flex-col bg-[#121418] text-white">
          <header className="flex flex-shrink-0 items-center gap-4 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-white/65 transition hover:bg-white/[0.07] hover:text-white"
              aria-label="Close history"
            >
              <i className="fa-solid fa-xmark text-xl"></i>
            </button>

            <h2 className="mb-0 text-2xl font-black tracking-tight text-white">
              History
            </h2>

            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={onRefresh}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-white/40 transition hover:bg-white/[0.07] hover:text-white"
                aria-label="Refresh history"
              >
                <i className="fa-solid fa-rotate-right text-sm"></i>
              </button>

              <button
                type="button"
                onClick={onNewChat}
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-black transition hover:bg-white/90"
                aria-label="New chat"
              >
                <i className="fa-solid fa-plus text-sm"></i>
              </button>
            </div>
          </header>

          <div className="grid grid-cols-2 border-b border-white/[0.065] px-5">
            <button
              type="button"
              className="relative py-3 text-left text-base font-black text-white"
            >
              Chats
              <span className="absolute bottom-0 left-0 h-0.5 w-12 bg-white/80"></span>
            </button>

            <button
              type="button"
              className="relative py-3 text-left text-base font-black text-white/38"
            >
              Actions
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col px-5 pb-5">
            <div className="mb-4 mt-4 rounded-xl bg-white/[0.055] px-3 py-2">
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                bordered={false}
                prefix={
                  <i className="fa-solid fa-magnifying-glass mr-2 text-base text-white/35"></i>
                }
                placeholder="Search history"
                className="copilot-history-search"
              />
            </div>

            <div className="copilot-history-scroll min-h-0 flex-1 overflow-y-auto">
              <HistoryList
                items={filteredChats}
                loading={loading}
                selectedSessionId={selectedSessionId}
                onSelectSession={onSelectSession}
                onRenameSession={openRenameModal}
                onDeleteSession={confirmDeleteSession}
              />
            </div>
          </div>
        </div>
      </Drawer>

      <Modal
        title="Rename chat"
        open={renameModalOpen}
        onCancel={closeRenameModal}
        onOk={submitRename}
        okText="Save"
        confirmLoading={renameSaving}
        centered
        destroyOnClose
        className="copilot-rename-modal"
      >
        <div className="py-3">
          <Input
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            onPressEnter={submitRename}
            maxLength={180}
            placeholder="Chat title"
          />
        </div>
      </Modal>
    </>
  );
}